import { Icon } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import { InputField, SelectSimple } from '@advanza/input'
import { fetchService } from 'actions/services'
import ServicesSelect from 'components/services/ServicesSelect'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import React, { useEffect } from 'react'
import DatePicker from '../DatePicker'
import './promoOfferInput.module.css'

// Configuration for offer types - easily extensible for new offer types
const OFFER_TYPES_CONFIG = {
    'one-time-leads': {
        displayName: 'One-time free leads',
        defaultDiscountType: 'amount',
        hasExpiryDate: true,
        hasPromoLength: false,
        fields: ['type', 'value', 'expiryDate'],
    },
    'recurring-leads': {
        displayName: 'Recurring free leads',
        defaultDiscountType: 'amount',
        hasExpiryDate: false,
        hasPromoLength: true,
        fields: ['type', 'value', 'promo_length'],
    },
    'subscription-discount': {
        displayName: 'Subscription discount',
        defaultDiscountType: 'percentage',
        hasExpiryDate: false,
        hasPromoLength: true,
        fields: ['type', 'value', 'promo_length'],
    },
    // Runs until a fixed calendar date (endDate) instead of for N months. Mutually
    // exclusive with 'subscription-discount' on the same promo. endDate is the
    // discount end promised to the provider, NOT the claim deadline (expiryDate).
    'subscription-discount-fixed-date': {
        displayName: 'Subscription discount until date',
        defaultDiscountType: 'percentage',
        hasExpiryDate: false,
        hasPromoLength: false,
        hasEndDate: true,
        fields: ['type', 'value', 'endDate'],
    },
    'subscription-overwrite-price': {
        displayName: 'Subscription overwrite price',
        defaultDiscountType: 'amount',
        hasExpiryDate: false,
        hasPromoLength: false,
        fields: ['value'],
    },
    'custom-lead-prices': {
        displayName: 'Custom lead prices',
        defaultDiscountType: 'amount',
        hasExpiryDate: false,
        hasPromoLength: false,
        fields: ['prices'],
    },
}

const LEAD_TYPE_OPTIONS = [
    { value: 'email', name: 'Email' },
    { value: 'phone', name: 'Phone' },
    { value: 'website', name: 'Website' },
]

// Generate KEY_OPTIONS from configuration
const KEY_OPTIONS = Object.keys(OFFER_TYPES_CONFIG).map((key) => ({
    value: key,
    name: OFFER_TYPES_CONFIG[key].displayName,
}))

const DISCOUNT_TYPE_OPTIONS = [
    { value: 'percentage', name: 'percentage' },
    { value: 'amount', name: 'amount' },
]

// An infinite promo length is canonically null, but the UI state may also hold the
// legacy string 'null' or the display symbol '∞'.
const isInfinitePromoLength = (promoLength) =>
    promoLength === null || promoLength === 'null' || promoLength === '∞'

function CustomLeadPriceRow({ priceEntry, onPriceChange, onRemove }) {
    const dispatch = useAppDispatch()
    const serviceId = priceEntry.service_id
    const servicesEntities = useAppSelector((state) => state.services.entities)
    const service = serviceId ? servicesEntities.services?.[serviceId] : null
    const questions = servicesEntities.questions || {}
    const optionsEntities = servicesEntities.options || {}

    useEffect(() => {
        if (serviceId) {
            dispatch(fetchService(serviceId))
        }
    }, [serviceId, dispatch])

    let optionItems = []
    if (service?.questions) {
        let optionIds = []
        service.questions.forEach((qId) => {
            const question = questions[qId]
            if (question?.options) {
                optionIds = optionIds.concat(question.options)
            }
        })
        optionItems = optionIds
            .filter((id) => optionsEntities[id])
            .map((id) => ({
                value: id,
                name: optionsEntities[id].value,
            }))
    }

    const leadType = (() => {
        if (priceEntry.is_website_click) {
            return 'website'
        }
        if (priceEntry.is_phone_click) {
            return 'phone'
        }
        return 'email'
    })()

    return (
        <Row
            className="v-center"
            style={{
                marginBottom: '8px',
                padding: '8px 12px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
            }}>
            <Col className="grow" style={{ marginRight: '8px' }}>
                <ServicesSelect
                    value={serviceId || null}
                    multiple={false}
                    placeholder="Service"
                    useSelectedCountry
                    onChangeValue={(services) => {
                        const newServiceId = services.length > 0 ? parseInt(services[0], 10) : null
                        onPriceChange({
                            ...priceEntry,
                            service_id: newServiceId,
                            option_id: null,
                        })
                    }}
                />
            </Col>
            <Col className="grow" style={{ marginRight: '8px' }}>
                <SelectSimple
                    value={priceEntry.option_id || '_hidden_'}
                    hiddenOption="All options"
                    placeholder="Option"
                    options={optionItems}
                    alwaysControlValue
                    onChange={(e) => {
                        const val = e.target.value
                        onPriceChange({
                            ...priceEntry,
                            option_id: val && val !== '_hidden_' ? parseInt(val, 10) : null,
                        })
                    }}
                />
            </Col>
            <Col className="grow" style={{ marginRight: '8px' }}>
                <InputField
                    placeholder="Price (€)"
                    type="number"
                    step="0.01"
                    value={
                        priceEntry.amount !== undefined && priceEntry.amount !== null
                            ? String(priceEntry.amount)
                            : ''
                    }
                    onChange={(e) => {
                        onPriceChange({
                            ...priceEntry,
                            amount: parseFloat(e.target.value) || 0,
                        })
                    }}
                    autoComplete="off"
                />
            </Col>
            <Col style={{ marginRight: '8px', minWidth: '120px' }}>
                <SelectSimple
                    value={leadType}
                    onChange={(e) => {
                        onPriceChange({
                            ...priceEntry,
                            is_website_click: e.target.value === 'website',
                            is_phone_click: e.target.value === 'phone',
                        })
                    }}
                    options={LEAD_TYPE_OPTIONS}
                />
            </Col>
            <Col c="auto">
                <span
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onRemove()
                    }}
                    style={{
                        color: '#d32f2f',
                        cursor: 'pointer',
                        fontSize: '18px',
                    }}>
                    <Icon>close</Icon>
                </span>
            </Col>
        </Row>
    )
}

/**
 * Component for managing promo offers
 * Converts between array format and array of {key, value, discountType, expiryDate, amount, percentage} objects
 */
class PromoOfferInput extends React.Component {
    constructor(props) {
        super(props)
        this.lastGeneratedJSON = null
        this.initialValueParsed = false
        // JSON columns are automatically decoded to arrays/objects by CakePHP
        // Handle arrays/objects directly, no stringification needed
        const parsedPairs = this.parseValue(props.value || '')
        // Add unique IDs to each pair for stable React keys
        const pairsWithIds = parsedPairs.map((pair, index) => ({
            ...pair,
            _id: `pair_${Date.now()}_${index}_${Math.random()}`,
        }))
        this.state = {
            pairs: pairsWithIds,
            focusedPromoLengthIndex: null, // Track which promo_length input is focused
            showPromoLengthInfoIndex: null, // Track which info tooltip is shown
        }
        // Mark that we've parsed the initial value
        const hasValue =
            props.value &&
            ((Array.isArray(props.value) && props.value.length > 0) ||
                (typeof props.value === 'object' &&
                    props.value !== null &&
                    Object.keys(props.value).length > 0) ||
                (typeof props.value === 'string' &&
                    props.value.trim() !== '' &&
                    props.value.trim() !== '[]' &&
                    props.value.trim() !== '{}'))
        if (hasValue) {
            this.initialValueParsed = true
        }
        this.promoLengthInputRefs = {} // Store refs for each promo_length input
        this.promoLengthInfoRefs = {} // Store refs for each info icon container
        this.togglePromoLengthInfo = this.togglePromoLengthInfo.bind(this)
        this.handleClickOutside = this.handleClickOutside.bind(this)
    }

    componentDidMount() {
        // Always check if we have a value to parse (in case entity loads after mount)
        this.checkAndUpdateValue(this.props.value)
        document.addEventListener('mousedown', this.handleClickOutside)
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside)
    }

    handleClickOutside(event) {
        // Close info tooltip if click is outside the info icon container
        if (this.state.showPromoLengthInfoIndex !== null) {
            const infoRef = this.promoLengthInfoRefs[this.state.showPromoLengthInfoIndex]
            if (infoRef && !infoRef.contains(event.target)) {
                this.setState({ showPromoLengthInfoIndex: null })
            }
        }
    }

    togglePromoLengthInfo(index, e) {
        e.stopPropagation()
        this.setState({
            showPromoLengthInfoIndex: this.state.showPromoLengthInfoIndex === index ? null : index,
        })
    }

    componentDidUpdate(prevProps) {
        // Update internal state when value prop changes from external source (e.g., when loading entity)
        // Skip update if the value matches what we just generated (to avoid resetting during user input)
        if (prevProps.value !== this.props.value) {
            // Check and update if we have a new value
            this.checkAndUpdateValue(this.props.value)
        }
    }

    checkAndUpdateValue(value) {
        if (!value) {
            // If value is empty and we have pairs, don't clear them (might be user input)
            // Only clear if we're sure it's an external change
            return
        }

        // JSON columns are automatically decoded to arrays/objects by CakePHP
        // Handle arrays/objects directly, no stringification needed
        const incomingValue = value

        // Check if incoming value has data
        let incomingHasData = false
        if (Array.isArray(incomingValue) && incomingValue.length > 0) {
            incomingHasData = true
        } else if (
            incomingValue &&
            typeof incomingValue === 'object' &&
            incomingValue !== null &&
            Object.keys(incomingValue).length > 0
        ) {
            incomingHasData = true
        } else if (
            typeof incomingValue === 'string' &&
            incomingValue.trim() !== '' &&
            incomingValue.trim() !== '[]' &&
            incomingValue.trim() !== '{}'
        ) {
            // Legacy string format support
            try {
                const parsed = JSON.parse(incomingValue)
                if (Array.isArray(parsed) && parsed.length > 0) {
                    incomingHasData = true
                } else if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
                    incomingHasData = true
                }
            } catch (e) {
                // Not valid JSON, skip
            }
        }

        if (!incomingHasData) {
            return
        }

        // Normalize both values for comparison using JSON stringification
        const normalizeValue = (val) => {
            if (!val) return ''
            let parsed = null
            if (Array.isArray(val) || (typeof val === 'object' && val !== null)) {
                parsed = val
            } else if (typeof val === 'string') {
                const trimmed = val.trim()
                if (trimmed === '' || trimmed === '[]' || trimmed === '{}') {
                    return ''
                }
                try {
                    parsed = JSON.parse(val)
                } catch (e) {
                    return String(val).trim()
                }
            } else {
                return String(val).trim()
            }

            // Re-stringify with consistent formatting for comparison
            try {
                return JSON.stringify(parsed)
            } catch (e) {
                return ''
            }
        }

        // Get current array from state
        const currentArray = this.buildJSON(this.state.pairs)
        const normalizedCurrent = normalizeValue(currentArray)
        const normalizedIncoming = normalizeValue(incomingValue)

        // Always update if:
        // 1. Current state is empty but incoming has data
        // 2. Incoming value is different from current (and not empty)
        // 3. We haven't just generated this array ourselves (check lastGeneratedJSON)
        const shouldUpdate =
            (this.state.pairs.length === 0 && incomingHasData) ||
            (incomingHasData &&
                normalizedIncoming !== normalizedCurrent &&
                normalizedIncoming !== this.lastGeneratedJSON)

        if (shouldUpdate) {
            this.lastGeneratedJSON = null
            const parsedPairs = this.parseValue(incomingValue)
            // Preserve existing IDs or generate new ones
            const pairsWithIds = parsedPairs.map((pair, index) => {
                const existingPair = this.state.pairs[index]
                return {
                    ...pair,
                    _id: existingPair?._id || `pair_${Date.now()}_${index}_${Math.random()}`,
                }
            })
            this.setState({
                pairs: pairsWithIds,
            })
            this.initialValueParsed = true
        }
    }

    // Map old backend keys to new offer type keys (for backward compatibility)
    mapKeyFromBackend(key, val) {
        // Handle old format: "leads" -> determine if one-time or recurring
        if (key === 'leads') {
            const promoLength = val?.promo_length
            const hasExpiryDate = val?.expiryDate && val.expiryDate.trim() !== ''
            // If has expiryDate, it's one-time
            if (hasExpiryDate) {
                return 'one-time-leads'
            }
            // If promo_length is 0 or null/undefined, determine based on context
            if (promoLength === null || promoLength === undefined || promoLength === '') {
                return 'recurring-leads' // Default to recurring if no promo_length
            }
            const numPromoLength = parseInt(promoLength, 10)
            if (isNaN(numPromoLength) || numPromoLength > 0) {
                return 'recurring-leads'
            }
            return 'one-time-leads' // promo_length === 0 means one-time
        }
        // Handle old format: "discount" -> subscription-discount
        if (key === 'discount') {
            return 'subscription-discount'
        }
        // If key is already in new format, return as-is
        if (OFFER_TYPES_CONFIG[key]) {
            return key
        }
        // Handle old camelCase format for backward compatibility
        if (key === 'recurringLeads') return 'recurring-leads'
        if (key === 'oneTimeLeads') return 'one-time-leads'
        if (key === 'subscriptionDiscount') return 'subscription-discount'
        if (key === 'subscriptionOverwritePrice') return 'subscription-overwrite-price'
        if (key === 'customLeadPrices') return 'custom-lead-prices'
        return key
    }

    parseValue(value) {
        if (!value) {
            return []
        }

        let parsed = null

        // Handle if value is already an object
        if (typeof value === 'object' && value !== null) {
            parsed = value
        } else if (typeof value === 'string') {
            // Handle empty JSON object/array string
            const trimmed = value.trim()
            if (trimmed === '' || trimmed === '{}' || trimmed === '[]' || trimmed === 'null') {
                return []
            }
            // Try to parse as JSON first
            try {
                parsed = JSON.parse(value)

                // Handle double-encoded JSON (e.g. "[{...}]" string inside a string)
                if (typeof parsed === 'string') {
                    try {
                        const doubleParsed = JSON.parse(parsed)
                        if (
                            doubleParsed &&
                            (Array.isArray(doubleParsed) || typeof doubleParsed === 'object')
                        ) {
                            parsed = doubleParsed
                        }
                    } catch (e) {
                        // It was just a string, keep original parsed value
                    }
                }

                // If parsed to empty object or empty array, return empty array
                if (Array.isArray(parsed) && parsed.length === 0) {
                    return []
                }
                if (
                    parsed &&
                    typeof parsed === 'object' &&
                    !Array.isArray(parsed) &&
                    Object.keys(parsed).length === 0
                ) {
                    return []
                }
            } catch (e) {
                // Not JSON, will try legacy format below
                parsed = null
            }
        }

        // Handle new array format: [{"discount": {...}}, {"discount": {...}}, {"leads": {...}}]
        if (Array.isArray(parsed)) {
            const pairs = []
            parsed.forEach((offerObj) => {
                if (typeof offerObj === 'object' && offerObj !== null) {
                    // Each object has a single key (offer type) with offer details as value
                    Object.keys(offerObj).forEach((backendKey) => {
                        // Map backend key to new offer type key
                        const newKey = this.mapKeyFromBackend(backendKey, offerObj[backendKey])
                        const pair = { key: newKey, value: '' }
                        const val = offerObj[backendKey]

                        // Common parsing for all types using configuration
                        if (typeof val === 'object' && val !== null) {
                            // Set discountType based on offer type configuration
                            const offerConfig = OFFER_TYPES_CONFIG[newKey]
                            pair.discountType =
                                val.type ||
                                (offerConfig ? offerConfig.defaultDiscountType : 'percentage')

                            // Always check for value
                            pair.value =
                                val.value !== undefined && val.value !== null
                                    ? String(val.value)
                                    : ''

                            // Always check for expiryDate
                            pair.expiryDate =
                                val.expiryDate !== undefined && val.expiryDate !== null
                                    ? String(val.expiryDate)
                                    : ''

                            // Always check for endDate (fixed-date subscription discount)
                            pair.endDate =
                                val.endDate !== undefined && val.endDate !== null
                                    ? String(val.endDate)
                                    : ''

                            // Always check for promo_length (5th attribute)
                            // Preserve null to represent infinite
                            pair.promoLength = (() => {
                                if (val.promo_length === undefined) {
                                    return ''
                                }
                                if (val.promo_length === null) {
                                    return null
                                }
                                return String(val.promo_length)
                            })()

                            // Always check for amount/percentage (specific to leads usually)
                            pair.amount =
                                val.amount !== undefined && val.amount !== null
                                    ? String(val.amount)
                                    : ''
                            pair.percentage =
                                val.percentage !== undefined && val.percentage !== null
                                    ? String(val.percentage)
                                    : ''

                            // Preserve prices array for custom-lead-prices
                            if (newKey === 'custom-lead-prices' && Array.isArray(val.prices)) {
                                pair.prices = val.prices
                            }
                        } else {
                            // Legacy format support
                            const strVal = String(val)
                            if (backendKey === 'discount') {
                                if (strVal.startsWith('percentage:')) {
                                    pair.discountType = 'percentage'
                                    pair.value = strVal.substring('percentage:'.length)
                                } else if (strVal.startsWith('amount:')) {
                                    pair.discountType = 'amount'
                                    pair.value = strVal.substring('amount:'.length)
                                } else {
                                    pair.discountType = 'percentage'
                                    pair.value = strVal
                                }
                            } else {
                                pair.value = strVal
                            }
                            pair.expiryDate = ''
                        }

                        pairs.push(pair)
                    })
                }
            })
            return pairs
        }

        // Handle old object format for backward compatibility: {"discount": {...}, "leads": {...}}
        if (parsed && typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            // Convert JSON object to pairs array
            const pairs = []
            Object.keys(parsed).forEach((backendKey) => {
                // Map backend key to new offer type key
                const newKey = this.mapKeyFromBackend(backendKey, parsed[backendKey])
                const pair = { key: newKey, value: '' }
                const val = parsed[backendKey]

                // Get configuration for the mapped key
                const offerConfig = OFFER_TYPES_CONFIG[newKey]

                if (typeof val === 'object' && val !== null) {
                    // Use configuration for default discount type
                    pair.discountType =
                        val.type || (offerConfig ? offerConfig.defaultDiscountType : 'percentage')
                    pair.value =
                        val.value !== undefined && val.value !== null ? String(val.value) : ''

                    // Add expiryDate if configured
                    if (offerConfig && offerConfig.hasExpiryDate) {
                        pair.expiryDate =
                            val.expiryDate !== undefined && val.expiryDate !== null
                                ? String(val.expiryDate)
                                : ''
                    }

                    // Add endDate if configured (fixed-date subscription discount)
                    if (offerConfig && offerConfig.hasEndDate) {
                        pair.endDate =
                            val.endDate !== undefined && val.endDate !== null
                                ? String(val.endDate)
                                : ''
                    }

                    // Add promo_length if configured
                    if (offerConfig && offerConfig.hasPromoLength) {
                        pair.promoLength = (() => {
                            if (val.promo_length === undefined) {
                                return ''
                            }
                            if (val.promo_length === null) {
                                return null
                            }
                            return String(val.promo_length)
                        })()
                    }
                } else {
                    // Legacy format: string value
                    const strVal = String(val)
                    if (strVal.startsWith('percentage:')) {
                        pair.discountType = 'percentage'
                        pair.value = strVal.substring('percentage:'.length)
                    } else if (strVal.startsWith('amount:')) {
                        pair.discountType = 'amount'
                        pair.value = strVal.substring('amount:'.length)
                    } else {
                        pair.discountType = offerConfig
                            ? offerConfig.defaultDiscountType
                            : 'percentage'
                        pair.value = strVal
                    }
                    pair.expiryDate = ''
                    pair.promoLength = ''
                }
                pairs.push(pair)
            })
            return pairs
        }

        // Legacy query string format support (only if value is a string and wasn't parsed as JSON)
        if (typeof value === 'string' && !parsed) {
            const cleaned = value.replace(/^[?&]/, '')
            if (!cleaned.trim()) {
                return []
            }
            try {
                const params = new URLSearchParams(cleaned)
                const pairs = []
                params.forEach((val, backendKey) => {
                    // Map backend key to new offer type key
                    const newKey = this.mapKeyFromBackend(backendKey, val)
                    const pair = { key: newKey, value: '' }
                    if (backendKey === 'discount') {
                        if (val.startsWith('percentage:')) {
                            pair.discountType = 'percentage'
                            pair.value = val.substring('percentage:'.length)
                        } else if (val.startsWith('amount:')) {
                            pair.discountType = 'amount'
                            pair.value = val.substring('amount:'.length)
                        } else {
                            pair.discountType = 'percentage'
                            pair.value = val
                        }
                    } else {
                        pair.value = val
                    }
                    pairs.push(pair)
                })
                return pairs
            } catch (error) {
                return []
            }
        }

        return []
    }

    buildJSON(pairs) {
        // Build array format using new keys: [{"one-time-leads": {...}}, {"recurring-leads": {...}}, {"subscription-discount": {...}}]
        const offersArray = []
        pairs.forEach((pair) => {
            const offerKey = (pair.key || '').trim()
            if (!offerKey) {
                return
            }

            // Get configuration for this offer type
            const offerConfig = OFFER_TYPES_CONFIG[offerKey]
            if (!offerConfig) {
                // Skip unknown offer types
                return
            }

            // Handle custom-lead-prices separately (uses prices array instead of value)
            if (offerKey === 'custom-lead-prices') {
                const finalOfferObj = {}
                finalOfferObj[offerKey] = {
                    prices: Array.isArray(pair.prices) ? pair.prices : [],
                }
                offersArray.push(finalOfferObj)
                return
            }

            // Build offer object based on configuration
            const offerObj = {
                type: pair.discountType || offerConfig.defaultDiscountType,
                value: pair.value
                    ? offerKey === 'subscription-discount' ||
                      offerKey === 'subscription-discount-fixed-date' ||
                      offerKey === 'subscription-overwrite-price'
                        ? parseFloat(pair.value) || 0
                        : parseInt(pair.value, 10) || 0
                    : 0,
            }

            // Add expiryDate if configured and has value
            if (
                offerConfig.hasExpiryDate &&
                pair.expiryDate !== undefined &&
                pair.expiryDate !== null &&
                pair.expiryDate !== ''
            ) {
                offerObj.expiryDate = pair.expiryDate
            }

            // Add endDate if configured and has value (fixed-date subscription discount)
            if (
                offerConfig.hasEndDate &&
                pair.endDate !== undefined &&
                pair.endDate !== null &&
                pair.endDate !== ''
            ) {
                offerObj.endDate = pair.endDate
            }

            // Add promo_length if configured
            if (offerConfig.hasPromoLength && pair.promoLength !== undefined) {
                if (isInfinitePromoLength(pair.promoLength) || pair.promoLength === '') {
                    offerObj.promo_length = null
                } else {
                    const promoLengthValue = parseInt(pair.promoLength, 10)
                    if (!isNaN(promoLengthValue) && promoLengthValue >= 0) {
                        offerObj.promo_length = promoLengthValue
                    } else {
                        offerObj.promo_length = null
                    }
                }
            }

            // For one-time-leads, explicitly set promo_length to 0 (one-time)
            if (offerKey === 'one-time-leads') {
                offerObj.promo_length = 0
            }

            // Build the final object with the offer key
            const finalOfferObj = {}
            finalOfferObj[offerKey] = offerObj
            offersArray.push(finalOfferObj)
        })
        // Return array directly - CakePHP's JsonType will encode it automatically
        return offersArray.length > 0 ? offersArray : []
    }

    handlePairChange(index, field, newValue) {
        const pairs = [...this.state.pairs]
        pairs[index] = { ...pairs[index], [field]: newValue }
        this.setState({ pairs })
        this.notifyChange(pairs)
    }

    handleAdd() {
        const newPair = {
            key: '',
            value: '',
            discountType: 'amount',
            expiryDate: '',
            endDate: '',
            promoLength: '',
            amount: '',
            percentage: '',
            prices: [],
            _id: `pair_${Date.now()}_${Math.random()}`,
        }
        const pairs = [...this.state.pairs, newPair]
        this.setState({ pairs })
        this.notifyChange(pairs)
    }

    handleRemove(index) {
        const pairs = this.state.pairs.filter((_, i) => i !== index)
        this.setState({ pairs })
        this.notifyChange(pairs)
    }

    // The two subscription-discount variants are mutually exclusive on a promo
    // (the backend rejects a promo carrying both): hide a variant from the picker
    // when another offer row already uses the other one.
    getKeyOptionsForIndex(index) {
        const otherKeys = this.state.pairs
            .filter((_, i) => i !== index)
            .map((otherPair) => otherPair.key)

        return KEY_OPTIONS.filter((option) => {
            if (
                option.value === 'subscription-discount' &&
                otherKeys.includes('subscription-discount-fixed-date')
            ) {
                return false
            }
            if (
                option.value === 'subscription-discount-fixed-date' &&
                otherKeys.includes('subscription-discount')
            ) {
                return false
            }
            return true
        })
    }

    notifyChange(pairs) {
        const offersArray = this.buildJSON(pairs)
        // Store normalized JSON string for comparison purposes
        this.lastGeneratedJSON = (() => {
            if (Array.isArray(offersArray) && offersArray.length > 0) {
                return JSON.stringify(offersArray)
            }
            if (typeof offersArray === 'object' && offersArray !== null) {
                return JSON.stringify(offersArray)
            }
            return ''
        })()
        const { onChange, name } = this.props
        if (onChange) {
            // Simulate event object for compatibility with EntityComponent's onChange
            // Pass array/object directly - CakePHP's JsonType will encode it automatically
            onChange({
                target: {
                    name,
                    value: offersArray,
                },
            })
        }
    }

    render() {
        const { pairs } = this.state
        const { error, placeholder } = this.props

        return (
            <div>
                {pairs.map((pair, index) => (
                    <div
                        key={pair._id || index}
                        style={{
                            marginBottom: '16px',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                        }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                            Offer {index + 1}
                        </h4>
                        <Row className="v-center">
                            <Col style={{ marginRight: '8px', minWidth: '180px' }}>
                                <SelectSimple
                                    placeholder="Select offer"
                                    value={pair.key || ''}
                                    options={this.getKeyOptionsForIndex(index)}
                                    alwaysControlValue
                                    emptyOption={false}
                                    onChange={(e) => {
                                        const newKey = e.target.value
                                        const updatedPair = { ...pair, key: newKey }
                                        // Set discountType based on offer type configuration
                                        const offerConfig = OFFER_TYPES_CONFIG[newKey]
                                        if (offerConfig) {
                                            updatedPair.discountType =
                                                offerConfig.defaultDiscountType
                                        }
                                        // Reset value when changing key
                                        if (pair.value === undefined) {
                                            updatedPair.value = ''
                                        }
                                        // Initialize prices array for custom-lead-prices
                                        if (newKey === 'custom-lead-prices') {
                                            updatedPair.prices = updatedPair.prices || []
                                        }
                                        const pairs = [...this.state.pairs]
                                        pairs[index] = updatedPair
                                        this.setState({ pairs })
                                        this.notifyChange(pairs)
                                    }}
                                    id={`q_params_${index}_key`}
                                    name={`q_params_${index}_key`}
                                />
                            </Col>
                            {/* Only show other fields if an offer type is selected */}
                            {pair.key && pair.key !== 'custom-lead-prices' && (
                                <>
                                    {/* Type is fixed based on offer type - no dropdown needed */}
                                    <Col className="grow" style={{ marginRight: '8px' }}>
                                        <InputField
                                            placeholder={(() => {
                                                if (
                                                    pair.key === 'recurring-leads' ||
                                                    pair.key === 'one-time-leads'
                                                ) {
                                                    return 'Number of leads'
                                                }
                                                if (
                                                    pair.key === 'subscription-discount' ||
                                                    pair.key === 'subscription-discount-fixed-date'
                                                ) {
                                                    return 'Discount percentage'
                                                }
                                                if (pair.key === 'subscription-overwrite-price') {
                                                    return 'Monthly price (ex VAT)'
                                                }
                                                return 'Value'
                                            })()}
                                            type="number"
                                            step={
                                                pair.key === 'subscription-overwrite-price'
                                                    ? '0.01'
                                                    : undefined
                                            }
                                            value={
                                                pair.value !== undefined && pair.value !== null
                                                    ? String(pair.value)
                                                    : ''
                                            }
                                            onChange={(e) =>
                                                this.handlePairChange(
                                                    index,
                                                    'value',
                                                    e.target.value,
                                                )
                                            }
                                            id={`q_params_${index}_value`}
                                            name={`q_params_${index}_value`}
                                            autoComplete="off"
                                        />
                                    </Col>
                                    {/* Recurring (months) - only show for recurring-leads and subscription-discount */}
                                    {(pair.key === 'recurring-leads' ||
                                        pair.key === 'subscription-discount') && (
                                        <Col style={{ marginRight: '8px', minWidth: '150px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                }}>
                                                <div style={{ flex: 1 }}>
                                                    <InputField
                                                        ref={(ref) => {
                                                            if (ref && ref.ref) {
                                                                this.promoLengthInputRefs[index] =
                                                                    ref.ref
                                                            }
                                                        }}
                                                        placeholder="Recurring (months)"
                                                        type={
                                                            isInfinitePromoLength(pair.promoLength)
                                                                ? 'text'
                                                                : 'number'
                                                        }
                                                        value={(() => {
                                                            if (
                                                                isInfinitePromoLength(
                                                                    pair.promoLength,
                                                                )
                                                            ) {
                                                                return '∞'
                                                            }
                                                            if (
                                                                pair.promoLength !== undefined &&
                                                                pair.promoLength !== null &&
                                                                pair.promoLength !== ''
                                                            ) {
                                                                return String(pair.promoLength)
                                                            }
                                                            return ''
                                                        })()}
                                                        min="0"
                                                        step="1"
                                                        readOnly={isInfinitePromoLength(
                                                            pair.promoLength,
                                                        )}
                                                        onFocus={(e) => {
                                                            this.setState({
                                                                focusedPromoLengthIndex: index,
                                                            })
                                                            // When focused, if showing infinity, clear the field for editing
                                                            if (
                                                                isInfinitePromoLength(
                                                                    pair.promoLength,
                                                                )
                                                            ) {
                                                                this.handlePairChange(
                                                                    index,
                                                                    'promoLength',
                                                                    '',
                                                                )
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            // Delay hiding button to check if focus moved to infinite button
                                                            setTimeout(() => {
                                                                const activeElement =
                                                                    document.activeElement
                                                                const isClickingButton =
                                                                    activeElement &&
                                                                    (activeElement.closest(
                                                                        'button[title="Set to infinite"]',
                                                                    ) ||
                                                                        activeElement.tagName ===
                                                                            'BUTTON')

                                                                if (!isClickingButton) {
                                                                    this.setState({
                                                                        focusedPromoLengthIndex:
                                                                            null,
                                                                    })
                                                                }

                                                                // On blur, ensure we have a valid value
                                                                const value = e.target.value
                                                                if (
                                                                    value === '∞' ||
                                                                    value === '' ||
                                                                    value === 'null'
                                                                ) {
                                                                    this.handlePairChange(
                                                                        index,
                                                                        'promoLength',
                                                                        null,
                                                                    )
                                                                } else {
                                                                    const numValue = parseInt(
                                                                        value,
                                                                        10,
                                                                    )
                                                                    if (
                                                                        !isNaN(numValue) &&
                                                                        numValue >= 0
                                                                    ) {
                                                                        this.handlePairChange(
                                                                            index,
                                                                            'promoLength',
                                                                            String(numValue),
                                                                        )
                                                                    } else {
                                                                        this.handlePairChange(
                                                                            index,
                                                                            'promoLength',
                                                                            '0',
                                                                        )
                                                                    }
                                                                }
                                                            }, 100)
                                                        }}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                            // If user types infinity symbol, set to null
                                                            if (value === '∞' || value === '') {
                                                                this.handlePairChange(
                                                                    index,
                                                                    'promoLength',
                                                                    null,
                                                                )
                                                            } else {
                                                                // Prevent negative values - remove any minus signs
                                                                if (value.includes('-')) {
                                                                    const positiveValue =
                                                                        value.replace(/-/g, '')
                                                                    const numValue = parseInt(
                                                                        positiveValue,
                                                                        10,
                                                                    )
                                                                    if (
                                                                        !isNaN(numValue) &&
                                                                        numValue >= 0
                                                                    ) {
                                                                        this.handlePairChange(
                                                                            index,
                                                                            'promoLength',
                                                                            String(numValue),
                                                                        )
                                                                    }
                                                                    return
                                                                }

                                                                const numValue = parseInt(value, 10)
                                                                // Only allow non-negative integers (0 or positive)
                                                                if (
                                                                    !isNaN(numValue) &&
                                                                    numValue >= 0
                                                                ) {
                                                                    this.handlePairChange(
                                                                        index,
                                                                        'promoLength',
                                                                        String(numValue),
                                                                    )
                                                                } else if (value === '') {
                                                                    // Allow empty string while typing
                                                                    this.handlePairChange(
                                                                        index,
                                                                        'promoLength',
                                                                        '',
                                                                    )
                                                                }
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            // Prevent minus, 'e', 'E', '+', and '.' keys from being entered
                                                            if (
                                                                e.key === '-' ||
                                                                e.key === 'e' ||
                                                                e.key === 'E' ||
                                                                e.key === '+' ||
                                                                e.key === '.'
                                                            ) {
                                                                e.preventDefault()
                                                                e.stopPropagation()
                                                            }
                                                        }}
                                                        id={`q_params_${index}_promoLength`}
                                                        name={`q_params_${index}_promoLength`}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                                {this.state.focusedPromoLengthIndex === index && (
                                                    <span
                                                        onMouseDown={(e) => {
                                                            // Prevent blur event when clicking the link
                                                            e.preventDefault()
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            // Set promo_length to null (infinite)
                                                            this.handlePairChange(
                                                                index,
                                                                'promoLength',
                                                                null,
                                                            )
                                                            // Keep focus on the input field and maintain focus state
                                                            setTimeout(() => {
                                                                if (
                                                                    this.promoLengthInputRefs[index]
                                                                ) {
                                                                    this.promoLengthInputRefs[
                                                                        index
                                                                    ].focus()
                                                                    this.setState({
                                                                        focusedPromoLengthIndex:
                                                                            index,
                                                                    })
                                                                }
                                                            }, 10)
                                                        }}
                                                        style={{
                                                            color: '#1976d2',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                            fontSize: '20px',
                                                            padding: '0 8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '44px',
                                                            height: '44px',
                                                        }}
                                                        title="Set to infinite">
                                                        ∞
                                                    </span>
                                                )}
                                                <div
                                                    ref={(el) =>
                                                        (this.promoLengthInfoRefs[index] = el)
                                                    }
                                                    style={{ position: 'relative' }}>
                                                    <Icon
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#666',
                                                            fontSize: '18px',
                                                        }}
                                                        onClick={(e) =>
                                                            this.togglePromoLengthInfo(index, e)
                                                        }>
                                                        info
                                                    </Icon>
                                                    {this.state.showPromoLengthInfoIndex ===
                                                        index && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                marginTop: '8px',
                                                                padding: '12px 16px',
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                boxShadow: '0 2px 8px #00000026',
                                                                zIndex: 1000,
                                                                minWidth: '280px',
                                                                maxWidth: '350px',
                                                            }}>
                                                            <p
                                                                style={{
                                                                    margin: '0 0 8px 0',
                                                                    fontWeight: 'bold',
                                                                }}>
                                                                Recurring Months:
                                                            </p>
                                                            <ul
                                                                style={{
                                                                    margin: 0,
                                                                    paddingLeft: '20px',
                                                                    fontSize: '14px',
                                                                }}>
                                                                <li style={{ marginBottom: '4px' }}>
                                                                    0 = one-time promo
                                                                </li>
                                                                <li style={{ marginBottom: '4px' }}>
                                                                    Infinite (∞) = promo recurs
                                                                    forever
                                                                </li>
                                                                <li>
                                                                    Any positive number = number of
                                                                    months the promo will recur
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    {/* Discount end date - only show for subscription-discount-fixed-date */}
                                    {pair.key === 'subscription-discount-fixed-date' && (
                                        <Col style={{ marginRight: '8px', minWidth: '200px' }}>
                                            <DatePicker
                                                placeholder="Free until (end date)"
                                                value={
                                                    pair.endDate && pair.endDate.trim()
                                                        ? pair.endDate
                                                        : null
                                                }
                                                onChange={(e) =>
                                                    this.handlePairChange(
                                                        index,
                                                        'endDate',
                                                        e.target.value || '',
                                                    )
                                                }
                                                name={`q_params_${index}_endDate`}
                                            />
                                        </Col>
                                    )}
                                    {/* Leads expiry date - only show for one-time-leads */}
                                    {pair.key === 'one-time-leads' && (
                                        <Col style={{ marginRight: '8px', minWidth: '200px' }}>
                                            <DatePicker
                                                placeholder="Leads expiry date"
                                                value={
                                                    pair.expiryDate && pair.expiryDate.trim()
                                                        ? pair.expiryDate
                                                        : null
                                                }
                                                onChange={(e) =>
                                                    this.handlePairChange(
                                                        index,
                                                        'expiryDate',
                                                        e.target.value || '',
                                                    )
                                                }
                                                name={`q_params_${index}_expiryDate`}
                                            />
                                        </Col>
                                    )}
                                    <Col c="auto">
                                        <span
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                this.handleRemove(index)
                                            }}
                                            style={{
                                                color: '#1976d2',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontSize: '14px',
                                            }}>
                                            Remove
                                        </span>
                                    </Col>
                                </>
                            )}
                            {/* Custom lead prices sub-form */}
                            {pair.key === 'custom-lead-prices' && (
                                <>
                                    <Col c="auto">
                                        <span
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                this.handleRemove(index)
                                            }}
                                            style={{
                                                color: '#1976d2',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontSize: '14px',
                                            }}>
                                            Remove
                                        </span>
                                    </Col>
                                </>
                            )}
                        </Row>
                        {pair.key === 'custom-lead-prices' && (
                            <div style={{ marginTop: '12px' }}>
                                {(pair.prices || []).map((priceEntry, priceIndex) => (
                                    <CustomLeadPriceRow
                                        key={priceIndex}
                                        priceEntry={priceEntry}
                                        onPriceChange={(updated) => {
                                            const prices = [...(pair.prices || [])]
                                            prices[priceIndex] = updated
                                            this.handlePairChange(index, 'prices', prices)
                                        }}
                                        onRemove={() => {
                                            const prices = (pair.prices || []).filter(
                                                (_, i) => i !== priceIndex,
                                            )
                                            this.handlePairChange(index, 'prices', prices)
                                        }}
                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const prices = [
                                            ...(pair.prices || []),
                                            {
                                                service_id: null,
                                                option_id: null,
                                                amount: 0,
                                                is_website_click: false,
                                                is_phone_click: false,
                                            },
                                        ]
                                        this.handlePairChange(index, 'prices', prices)
                                    }}
                                    className="plain c-blue"
                                    style={{ marginTop: '4px' }}>
                                    <Icon>add</Icon> add price entry
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                <Row>
                    <Col>
                        <button
                            type="button"
                            onClick={() => this.handleAdd()}
                            className="plain c-blue"
                            style={{ marginTop: '8px' }}>
                            <Icon>add</Icon> add offer
                        </button>
                    </Col>
                </Row>
                {error && (
                    <div className="error" style={{ marginTop: '8px' }}>
                        Error: {error}
                    </div>
                )}
            </div>
        )
    }
}

export default PromoOfferInput
